import * as ko from "knockout";
import {template as tpl, viewModel as MutablePermission} from "./mutable";
import {Organization} from "./system-permission/Organization";
import {IOrganization, IRouteDefinitionParams, ISystem, ISystemPermission} from "../../index";
import {global} from "../../appContext";
import {getSearchSystemParams} from "../../util/request";
import {MAX_RESULTS_PER_PAGE, ObjectTypeId} from "../../constants";
import {createErrorMessage} from "../../util/misc";
import {REQUEST_MESSAGE} from "../../constants/Language";

export let template = tpl;

interface ICreatePermissionsParams {
    isValid: KnockoutObservable<boolean>
    getPermissions: () => IOrganization[]
    mutableOrganizations: KnockoutObservableArray<Organization>
}

export class viewModel extends MutablePermission {

    private readonly req_searchsystem = global.getPromiseRequest('searchsystem');

    constructor(params: ICreatePermissionsParams & IRouteDefinitionParams) {
        super(params);

        // hook up params' isValid observable
        this.kosub.subscribe(this.isValid, (bool: boolean) => params.isValid(bool));
        params.isValid(this.isValid());

        // hook up params' mutableOrganizations observable array
        if (params.mutableOrganizations) {
            this.organizations = params.mutableOrganizations;
        } else {
            params.mutableOrganizations = this.organizations = ko.observableArray<Organization>([]);
        }

        // hook up params' getPermissions callback
        params.getPermissions = this._getSubmitDataPermission.bind(this);

        this.init();
    }

    protected init() {
        this.isReady(false);
        this.user = null;

        const isMutableOrganizations = !!this.organizations().length;
        const isRouteParams = !!this.__routeParams?.['orgnr'];
        const isOnlyOneOrg = global.login.organizations().length == 1;

        if (isMutableOrganizations) {
            this.initRegular();
        } else if (isRouteParams) {
            this.initRouteParams();
        } else if (isOnlyOneOrg) {
            this.initOnlyOneOrg();
        } else {
            this.initRegular();
        }
    }

    private initRouteParams() {
        const orgnr = this.__routeParams?.['orgnr'];
        const systemid = Number(this.__routeParams?.systemid);
        const roleid = Number(this.__routeParams?.roleid);
        if (!orgnr) {
            this.initRegular();
            return;
        }

        const sp: ISystemPermission = {
            comment: '',
            companyid: orgnr,
            expirydate: '',
            id: 0,
            managerOf: 0,
            managerOfName: '',
            managerOfObjectTypeId: ObjectTypeId.Department,
            manual: true,
            recursive: false,
            role: null!,
            system: null!,
        };

        this.messageData({message: REQUEST_MESSAGE});

        this.req_searchsystem.request(getSearchSystemParams({
            orgnr: orgnr,
            resultsprpage: MAX_RESULTS_PER_PAGE,
        }))
            .then((data: ISystem[]) => {
                if (systemid) {
                    const system = data.find(s => s.id == systemid && !s.definitionreadonly);
                    if (system) {
                        sp.system = system;
                        if (roleid) {
                            const role = system.roles.find(r => r.id == roleid);
                            if (role) {
                                sp.role = role;
                                sp.recursive = role.recursive;
                            }
                        }
                    }
                }
            })
            .then(() => {
                const _org = this.createOrganization(orgnr);
                const mo = new Organization(this, _org, this.user);
                if (sp) {
                    mo.addExistingSystemPermission(sp)
                } else {
                    mo.addEmptySystemPermission();
                }
                mo.toggle(true);
                this.organizations.push(mo);
                this.messageData(null);
                this.isReady(true);
            })
            .catch((reason: string) => this.messageData(createErrorMessage(String(reason))));
    }

    private initOnlyOneOrg() {
        const orgId = global.login.selectedOrganizationId();
        if (!orgId) {
            this.initRegular();
            return;
        }
        const _org = this.createOrganization(orgId);
        const mo = new Organization(this, _org, this.user);
        mo.addEmptySystemPermission();
        mo.toggle(true);
        this.organizations.push(mo);
        this.isReady(true);
    }

    private initRegular() {
        this.isReady(true);
    }

    override dispose() {
        super.dispose();
        this.req_searchsystem.abort();
    }

}
